/**
 * Camlin Speech STREAMING ASR engine for the SDK mic path (7 Aug 2026).
 *
 * Live transcription while speaking, instead of the batch engine's
 * press-speak-POST turn (camlinAsr.ts). Same callback contract as
 * `sttFromMic` — the surrounding UX (listen controls, Companion voice
 * states) doesn't know which engine ran — plus the interim callback
 * actually firing continuously:
 *
 *   1. GET  <tokenUrl>?scope=stream       -> {token: "cst_…", expires_at}
 *      (server relay; the cst_ token is tenant-bound + streaming-only)
 *   2. Socket.IO connect to <streamUrl> with auth {key: cst_…}
 *   3. mic PCM -> 16 kHz int16 frames -> `live_audio_chunk`
 *   4. server emits `transcription` (committed words, LocalAgreement-2)
 *      and `partial_transcription` (unstable tail) -> interim callback
 *      renders grey/italic text growing as the visitor speaks
 *   5. server endpoints the turn on a natural pause and emits
 *      `live_turn_final` {text} -> the turn resolves exactly like a
 *      batch recognize would have, with lexicon biasing applied to
 *      every window decode server-side.
 *
 * The mic arming pattern (shared stream + AudioContext kept warm
 * between turns) is reused from the batch engine so tap-to-capture
 * latency stays in single-digit milliseconds.
 */

import { io, type Socket } from 'socket.io-client';
import { getSttEngineConfig } from './sttEngine';
import {
  cancelArmedStreamRelease,
  dispatchCapturePhase,
  downsample,
  getArmedAudioContext,
  getSharedStream,
  scheduleArmedStreamRelease,
  setSharedStream
} from './camlinAsr';

// ---------------------------------------------------------------------------
// Streaming token cache — separate from the batch cat_ cache on purpose:
// the two credentials answer different capabilities.
// ---------------------------------------------------------------------------

interface CachedToken {
  token: string;
  expiresAt: number; // epoch seconds
}

let cachedStreamToken: CachedToken | null = null;

const TOKEN_REFRESH_MARGIN_S = 30;

async function getStreamToken(tokenUrl: string): Promise<string> {
  const now = Date.now() / 1000;
  if (cachedStreamToken && cachedStreamToken.expiresAt - TOKEN_REFRESH_MARGIN_S > now) {
    return cachedStreamToken.token;
  }
  const url = tokenUrl + (tokenUrl.includes('?') ? '&' : '?') + 'scope=stream';
  const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`ASR stream token endpoint returned ${res.status}`);
  }
  const data = (await res.json()) as { token?: unknown; expires_at?: unknown };
  if (typeof data.token !== 'string' || !data.token.trim()) {
    throw new Error('ASR stream token endpoint returned no token');
  }
  cachedStreamToken = {
    token: data.token.trim(),
    expiresAt: typeof data.expires_at === 'number' ? data.expires_at : now + 240
  };
  return cachedStreamToken.token;
}

/** Test hook / hard reset. */
export function clearStreamTokenCache(): void {
  cachedStreamToken = null;
}

// ---------------------------------------------------------------------------
// PCM frame capture — 16 kHz int16 frames, ~85 ms each at 48 kHz input
// ---------------------------------------------------------------------------

const TARGET_SAMPLE_RATE = 16000;

// The whole turn gives up after this long no matter what (the server's
// endpointing should always fire first; this is the local backstop so a
// dead socket can't hold the mic forever).
const MAX_TURN_MS = 45000;

interface FrameCapture {
  stop: () => void;
}

function startFrameCapture(
  stream: MediaStream,
  audioContext: AudioContext,
  onFrame: (frame: ArrayBuffer) => void
): FrameCapture {
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(4096, 1, 1);
  const inputRate = audioContext.sampleRate;
  let stopped = false;

  processor.onaudioprocess = (event) => {
    if (stopped) return;
    const data = event.inputBuffer.getChannelData(0);
    const down = downsample(new Float32Array(data), inputRate, TARGET_SAMPLE_RATE);
    const pcm = new Int16Array(down.length);
    for (let i = 0; i < down.length; i++) {
      const s = Math.max(-1, Math.min(1, down[i]));
      pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    onFrame(pcm.buffer);
  };

  source.connect(processor);
  const sink = audioContext.createGain();
  sink.gain.value = 0;
  processor.connect(sink);
  sink.connect(audioContext.destination);

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      try {
        processor.disconnect();
        source.disconnect();
        sink.disconnect();
      } catch {
        /* already torn down */
      }
    }
  };
}

// ---------------------------------------------------------------------------
// The engine entry point — same signature as sttFromMic / sttFromMicCamlin
// ---------------------------------------------------------------------------

export async function sttFromMicCamlinStream(
  setDisplayText: (text: string) => void,
  setUserTalking: (value: boolean) => void,
  setRecord: (value: boolean) => void,
  setSpeak: (value: boolean) => void,
  askCamlin: (text: string) => void,
  sendMsgToBot: (msg: string, direction: string) => void,
  onInterimTranscript?: (text: string) => void
): Promise<void> {
  const engine = getSttEngineConfig();
  const tokenUrl = engine.tokenUrl;
  if (!tokenUrl) {
    throw new Error('Camlin streaming ASR engine is not configured (missing tokenUrl)');
  }
  const streamOrigin = (engine.streamUrl || window.location.origin).replace(/\/$/, '');

  const tapAt = performance.now();
  cancelArmedStreamRelease();

  let socket: Socket | null = null;
  let capture: FrameCapture | null = null;
  let turnTimer: ReturnType<typeof setTimeout> | null = null;

  const cleanup = async (outcome: 'ok' | 'error') => {
    if (turnTimer) {
      clearTimeout(turnTimer);
      turnTimer = null;
    }
    capture?.stop();
    capture = null;
    if (socket) {
      socket.removeAllListeners();
      socket.disconnect();
      socket = null;
    }
    setUserTalking(false);
    setSpeak(false);
    setRecord(false);
    dispatchCapturePhase('ended');
    window.currentAudioStream = null;
    if (outcome === 'error') {
      cancelArmedStreamRelease();
      const shared = getSharedStream();
      if (shared) {
        shared.getTracks().forEach((track) => track.stop());
        setSharedStream(null);
      }
    } else {
      scheduleArmedStreamRelease();
    }
  };

  try {
    setDisplayText('Initializing audio...');

    // Token first: unlike the batch engine (token needed only at POST
    // time), the streaming socket authenticates at connect, and we want
    // the connection open before frames start. Both still run in
    // parallel with mic acquisition.
    const tokenPromise = getStreamToken(tokenUrl);
    tokenPromise.catch(() => { /* surfaced at await below */ });

    let stream = getSharedStream();
    if (!stream) {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1
        }
      });
      setSharedStream(stream);
    }
    window.currentAudioStream = stream;

    const audioContext = await getArmedAudioContext();

    // Capture starts NOW — before the token round-trip and socket
    // connect — buffering frames locally and flushing once the socket
    // opens. Until 7 Aug (2.7.1) capture only started after connect
    // (~500-800 ms after tap), so a user who spoke immediately lost
    // their first words: Keith's "dead bit at the start". Bound at
    // ~10 s so a hung connect can't grow the buffer unbounded.
    const MAX_PENDING_FRAMES = 118;
    const pendingFrames: ArrayBuffer[] = [];
    let socketReady = false;
    capture = startFrameCapture(stream, audioContext, (frame) => {
      if (socketReady) {
        try {
          socket?.emit('live_audio_chunk', frame);
        } catch {
          /* disconnecting */
        }
      } else {
        pendingFrames.push(frame);
        if (pendingFrames.length > MAX_PENDING_FRAMES) pendingFrames.shift();
      }
    });
    setDisplayText('Speak now!');
    setUserTalking(true);
    setRecord(true);
    dispatchCapturePhase('capturing');
    console.log(`[camlinAsrStream] capture armed in ${Math.round(performance.now() - tapAt)}ms (pre-connect, buffering)`);

    const token = await tokenPromise;

    socket = io(streamOrigin, {
      transports: ['websocket'],
      reconnection: false,
      auth: { key: token }
    });

    await new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('live ASR connection timed out')), 10000);
      socket!.once('connect', () => {
        clearTimeout(timer);
        resolve();
      });
      socket!.once('connect_error', (err: Error) => {
        clearTimeout(timer);
        // A refused token reads as connect_error; drop the cache so the
        // next press mints fresh.
        clearStreamTokenCache();
        reject(new Error(`live ASR connection failed: ${err.message}`));
      });
    });

    // Interim rendering: committed words solid-ish, tentative tail
    // appended — both travel through the one interim callback; the final
    // turn text arrives on live_turn_final.
    let committedFull = '';
    let lastTentative = '';
    // Evidence that the pipeline heard SOMETHING this turn (any interim
    // text, or acoustic energy reported by the server) — decides whether
    // an empty final reads "no speech detected" or "didn't catch that".
    let heardSomething = false;
    const pushInterim = () => {
      const text = [committedFull, lastTentative].filter(Boolean).join(' ').trim();
      if (text && onInterimTranscript) {
        onInterimTranscript(text);
      }
    };

    socket.on('transcription', (data: { committed_full?: string }) => {
      if (typeof data?.committed_full === 'string') {
        committedFull = data.committed_full;
        if (committedFull.trim()) heardSomething = true;
        pushInterim();
      }
    });
    socket.on('partial_transcription', (data: { tentative?: string; committed_full?: string; rms?: number }) => {
      if (typeof data?.committed_full === 'string') {
        committedFull = data.committed_full;
      }
      lastTentative = typeof data?.tentative === 'string' ? data.tentative : '';
      if (lastTentative.trim() || committedFull.trim() || (typeof data?.rms === 'number' && data.rms >= 0.01)) {
        heardSomething = true;
      }
      pushInterim();
    });

    const turnDone = new Promise<{ text: string; reason: string }>((resolve, reject) => {
      socket!.on('live_turn_final', (data: { text?: string; reason?: string }) => {
        resolve({
          text: typeof data?.text === 'string' ? data.text.trim() : '',
          reason: typeof data?.reason === 'string' ? data.reason : 'unknown'
        });
      });
      socket!.once('disconnect', () => reject(new Error('live ASR connection dropped mid-turn')));
      turnTimer = setTimeout(() => {
        // Local backstop: ask the server to flush, then resolve with
        // whatever was committed rather than hanging the turn.
        try {
          socket?.emit('live_stop');
        } catch {
          /* socket already gone */
        }
        resolve({ text: committedFull.trim(), reason: 'client_timeout' });
      }, MAX_TURN_MS);
    });

    // Socket is open: flush everything captured while connecting, then
    // stream live. The server sees the same PCM it would have seen with
    // a zero-latency connect — nothing from the start of the turn is lost.
    for (const frame of pendingFrames) {
      socket.emit('live_audio_chunk', frame);
    }
    if (pendingFrames.length) {
      console.log(`[camlinAsrStream] flushed ${pendingFrames.length} buffered frames (${Math.round(performance.now() - tapAt)}ms after tap)`);
    }
    pendingFrames.length = 0;
    socketReady = true;

    const finalTurn = await turnDone;

    capture.stop();
    capture = null;
    setRecord(false);
    setUserTalking(false);
    dispatchCapturePhase('processing');

    const text = finalTurn.text.replace(/[.!?]+$/, '');
    if (text) {
      setDisplayText(`Processing: ${text}`);
      await askCamlin(text);
      sendMsgToBot(text, 'out');
      setDisplayText(`You said: ${text}`);
    } else {
      // "No speech" only when nothing was ever heard; if audio/interims
      // existed but the final decode came back empty, own it as OUR
      // miss, not the user's silence.
      setDisplayText(heardSomething
        ? "Didn't quite catch that — please try again."
        : 'No speech detected. Please try again.');
    }
    await cleanup('ok');
  } catch (error) {
    await cleanup('error');
    console.error('[camlinAsrStream] sttFromMicCamlinStream error:', error);
    throw error;
  }
}
